import { Component, Input, ElementRef } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { NmDatatableSettings, NmDatatableCommonSettings } from './datatable.interface';
import { commonSettings } from './datatable.config';
import { DataService } from '../../services/data.service';

/**
* Datatable is using datatable version 1.10.x with jquery
*/

declare let $: any;

@Component({
	selector: 'nm-datatable',
	templateUrl: './datatable.html'
})
export class NmDatatable {
	private _settings;
	private _reloadTable;
	@Input()
	set settings(settings: NmDatatableSettings) {
		// set default value
		this._settings = Object.assign({}, this._defaultComponentSettings, settings);

		// check if width is set
		for(let i of settings.fields) {
			if(i.width) {
				this._defaultDatatableSettings.autoWidth = false;
				break;
			}
		}

		// filter object
		let ofilter = (obj) => {
			let res = {};
			Object.keys(obj)
			.filter(k => obj[k] !== undefined || obj[k] !== null)
			.forEach(k => res[k] = obj[k]);
			return res;
		}

		if(settings.pluginOptions) {
			this._defaultDatatableSettings = Object.assign({}, this._defaultDatatableSettings, ofilter(settings.pluginOptions));
		}
	}
	get settings(): NmDatatableSettings { return this._settings; }

	@Input() id: string = 'nm-datatable';

	@Input()
	set reloadTable(reloadTable: boolean){
		this._reloadTable = reloadTable;
		//console.log('reload',reloadTable);
		if(reloadTable){
			this.reload();
		}
	}
	get reloadTable(): boolean{ return this._reloadTable}

	private _rawData: any[];
	public table: any; // datatable object

	public constructor(
		private elem: ElementRef,
		private router: Router,
		private dataService: DataService,
	) {}

	// default component settings
	protected _defaultComponentSettings: NmDatatableSettings = {
		modelName: '',
		enableActions: true,
		actions: [
			{ name: 'edit', icon: 'pencil', color: 'green', show: () => true },
			{ name: 'delete', icon: 'trash', color: 'red', show: () => true },
		],
	};

	// default datatable settings
	protected _defaultDatatableSettings = {
		paging: true,
		processing: true,
		//ordering: false, // disable sorting in datatable
		order: [[0, 'asc']],
		language: {
			processing: `<p class="text">Loading...</p>`
		},
		lengthMenu: [ [20, 30, 50, -1], [20, 30, 50, "All"] ],
		autoWidth: true
	}

	// init
	public ngAfterViewInit() {
		if(!this.settings) {
			console.error(`Undefined datatable settings`);
			return;
		}

		if(this.settings.data && this.settings.ajax) {
			console.error(`Both "data" and "ajax" are set, please choose one`);
			return;
		}

		// handle ajax
		if(this.settings.ajax) {
			this._ajaxHandler();
		}
		
		// handle behaviour subject
		if(this.settings.data) {
			this.settings.data.subscribe(data => {
				this._rawData = data;

				if(this.settings.dataMassage)
					data = this.settings.dataMassage(data);

				this._dataHandler(data);
			});
		}
	}

	// handle ajax
	private _ajaxHandler() {
		let self = this;

		let _url = commonSettings.ajaxUrlMassage?
			commonSettings.ajaxUrlMassage(self.settings.ajax.url):
			self.settings.ajax.url;

		let settings = Object.assign({}, this._defaultDatatableSettings, {
			columns: this._makeColumns(),
			serverSide: true,
			ajax: {
				method: self.settings.ajax.method || 'GET',
				url: _url,
				beforeSend: function(xhr, settings) {
					// common beforeSend from config
					if(commonSettings.ajaxBeforeSend)
						commonSettings.ajaxBeforeSend(xhr, settings);

					// independent beforeSend from component
					if(self.settings.ajaxBeforeSend)
						self.settings.ajaxBeforeSend(xhr, settings);
				},
				complete: function(xhr, status) {
					// common afterSend from config
					if(commonSettings.ajaxAfterSend)
						commonSettings.ajaxAfterSend(xhr, status);

					// independent aferSend from component
					if(self.settings.ajaxAfterSend)
						self.settings.ajaxAfterSend(xhr, status);
				},
				dataSrc: function(json) {
					let data = json.data;
					self._rawData = data;
					return self._dataMassage(data);
				}
			},
			searchCols: this._makeSearchCols()
		});
		this._initDatatable(settings);
	}

	// handle behaviourSubject data
	private _dataHandler(data) {
		let self = this;

		let settings = Object.assign({}, this._defaultDatatableSettings, {
			columns: this._makeColumns(),
			data: self._dataMassage(data),
			rowId: 'id'
		});

		if(!this.table) {
			this._initDatatable(settings);
		}
		else {
			this.table.clear();
			this.table.rows.add(data);
			this.table.draw();
		}
	}

	// clicked action button
	private _clickedActionButton;
	private _clickedActionName;

	// init datatable and event
	private _initDatatable(settings) {
		let self = this;
		this.table = $(self.elem.nativeElement).find('.datatable').DataTable(settings);
		$(self.elem.nativeElement).find('.datatable').on('click', '.action-button', function() {
			let btn = $(this);
			let id = btn.data('id');
			let action = self.settings.actions.find(v => v.name == btn.data('action'));
			let data = self._rawData.find(v => v.id == id);

			self._clickedActionButton = btn;
			self._clickedActionName = action.name;

			let result;
			if(action && action.callback) {
				result = action.callback.call(self, id, data);
			}
			else {
				if(commonSettings.actionButtonCallback[btn.data('action')]) {
					result = commonSettings.actionButtonCallback[btn.data('action')]
						.call(self, id, data);
				}
				else {
					console.error(`
						Missing action.callback and commonSettings.actionButtonCallback,
						please ensure you have defined ${btn.data('action')}.callback in your .ts file or
						actionButtonCallback.${btn.data('action')} in datatable.config.
					`);
				}
			}

			let _afterCallback = reload => {
				if(reload === true)
					self.reload();
			}

			if(result instanceof Promise) {
				result.then(reload => {
					_afterCallback(reload);
				});
			}
			else {
				_afterCallback(result);
			}
		});
	}

	// data massage
	private _dataMassage(data) {
		// common dataMassage from config
		if(commonSettings.ajaxDataMassage)
			data = commonSettings.ajaxDataMassage(data);

		// independent dataMassage from component
		if(this.settings.ajaxDataMassage)
			data = this.settings.ajaxDataMassage(data);

		// action buttons
		if(this.settings.enableActions)
			data = this._addActionButtons(data);

		return data;
	}

	// make column
	private _makeColumns() {
		let columns = this.settings.fields.map((v, i) => ({
			name: v.data !== undefined? v.name :null,
			data: v.data !== undefined? v.data : v.name,
			orderable: v.sortable !== undefined? v.sortable: i == 0,
			searchable: v.searchable !== undefined? v.searchable: false,
			width: v.width || ''
		}));

		// action buttons
		if(this.settings.enableActions)
			columns.push({ data: '_actionButtons',name: '_actionButtons', orderable: false, searchable: false, width: '10%'} );

		// mass action buttons
		if(this.settings.enableMassActions)
			columns.unshift({ data: '_massActionButtons',name: '_massActionButtons', orderable: false, searchable: false, width: '5%' });

		return columns;
	}

	private _makeSearchCols() {
		let columns = this.settings.fields.map((v, i) => (
			v.search?{search: v.search,regex:v.regex?true:false}:null
		));

		// action buttons
		if(this.settings.enableActions)
			columns.push(null);

		// mass action buttons
		if(this.settings.enableMassActions)
			columns.unshift(null);

		return columns;
	}

	// add action buttons
	private _addActionButtons(data) {
		return data.map(v => {
			let html = '';
			this.settings.actions.forEach(action => {
				if(action.show(v.id, v)) {
					html += `
						<a class="${action.color} action-button" data-action="${action.name}" data-id="${v.id}">
							<i class="fa fa-${action.icon} bigger-130"></i> ${action.label || ''}
						</a>
					`;
				}
			});
			v._actionButtons = `<div class="action-buttons">${html}</div>`;
			return v;
		});
	}

	// reload datatable
	public reload() {
		if(this.settings.ajax) {
			this.table.ajax.reload();
		}
		else {
			if(this._clickedActionName == 'delete') {
				let tr = this._clickedActionButton.parents('tr');
				this.table.row(tr).remove().draw();
			}
		}
	}

} // end of DatatableComponent



// WEBPACK FOOTER //
// ./src/widgets/datatable/datatable.component.ts